import api from './api';
import { Atividade } from '../types';

export interface AtividadeResponse {
  success: boolean;
  message?: string;
  atividades?: Atividade[];
  atividade?: Atividade;
  paginacao?: {
    total: number;
    pagina: number;
    limite: number;
    paginas: number;
  };
}

/**
 * Serviço para gerenciar atividades
 */
const atividadeService = {
  /**
   * Obter todas as atividades com filtros e paginação
   */
  obterTodas: async (
    filtros?: {
      tarefa_id?: string;
      tipo?: string;
      usuario_origem_id?: string;
      data_inicio?: string;
      data_fim?: string;
    },
    paginacao?: {
      pagina?: number;
      limite?: number;
    }
  ): Promise<AtividadeResponse> => {
    try {
      const params = new URLSearchParams();
      
      // Adicionar filtros à query string
      if (filtros?.tarefa_id) {
        params.append('tarefa_id', filtros.tarefa_id);
      }
      
      if (filtros?.tipo) {
        params.append('tipo', filtros.tipo);
      }
      
      if (filtros?.usuario_origem_id) {
        params.append('usuario_origem_id', filtros.usuario_origem_id);
      }
      
      if (filtros?.data_inicio) {
        params.append('data_inicio', filtros.data_inicio);
      }
      
      if (filtros?.data_fim) {
        params.append('data_fim', filtros.data_fim);
      }
      
      // Adicionar parâmetros de paginação
      if (paginacao?.pagina) {
        params.append('pagina', paginacao.pagina.toString());
      }
      
      if (paginacao?.limite) {
        params.append('limite', paginacao.limite.toString());
      }
      
      const response = await api.get<AtividadeResponse>(`/atividades?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao obter atividades:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter atividades'
      };
    }
  },
  
  /**
   * Obter atividades de uma tarefa específica
   */
  obterPorTarefa: async (tarefaId: string): Promise<AtividadeResponse> => {
    try {
      const response = await api.get<AtividadeResponse>(`/atividades/tarefa/${tarefaId}`);
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao obter atividades da tarefa ${tarefaId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter atividades da tarefa'
      };
    }
  },
  
  /**
   * Obter detalhes de uma atividade específica
   */
  obterPorId: async (id: string): Promise<AtividadeResponse> => {
    try {
      const response = await api.get<AtividadeResponse>(`/atividades/${id}`);
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao obter atividade ${id}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter atividade'
      };
    }
  },
  
  /**
   * Marcar atividade como lida
   */
  marcarComoLida: async (id: string): Promise<AtividadeResponse> => {
    try {
      const response = await api.patch<AtividadeResponse>(`/atividades/${id}/lida`, { lida: true });
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao marcar atividade ${id} como lida:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao marcar atividade como lida'
      };
    }
  },
  
  /**
   * Marcar múltiplas atividades como lidas
   */
  marcarMultiplasComoLidas: async (ids: string[]): Promise<AtividadeResponse> => {
    try {
      const response = await api.patch<AtividadeResponse>('/atividades/lidas', { ids });
      return response.data;
    } catch (error: any) {
      console.error('Erro ao marcar múltiplas atividades como lidas:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao marcar atividades como lidas'
      };
    }
  }
};

export default atividadeService;
